import type { ChartCard, Timeframe } from '@/features/dashboard/api/schemas'
import { formatAbbrev } from '@/features/dashboard/format/format'
import {
  findSeries,
  latestDataIndex,
  metricSeries,
  OUTLOOK_SERIES,
  referenceSeries,
  seriesLabel,
} from './series'

/** One comparison in the expanded card's side panel. */
export type StatusRow = {
  label: string
  /** The percentage difference: positive when ahead of the benchmark. */
  pct: number
  /** On a euro card, the signed difference in euros too: '-€4.5M'. */
  diff: string | null
}

export type StatusSection = {
  title: string
  rows: StatusRow[]
  /** A figure leading the section as a row of its own, labelled `totalLabel`. */
  total?: string
  totalLabel?: string
}

/** The benchmarks the current value is compared to, in row order. */
const PANEL_DELTA_SERIES_NAMES = [
  'metric_revised_budget_upside',
  'metric_revised_budget_base',
  'metric_budget_upside',
  'metric_budget_base',
  'target',
]

/** The benchmarks the year-end outlook is compared to (`OUTLOOK_COMPARISON_SERIES_NAMES`). */
const OUTLOOK_COMPARISON_SERIES_NAMES = [
  'metric_revised_budget_upside',
  'metric_revised_budget_base',
]

const lastNumberIndex = (data: (number | null)[]) =>
  data.findLastIndex(value => typeof value === 'number')

/** Whether the Annual view's viewed year has an outlook, which then measures it. */
export const annualOutlookOf = (
  card: Pick<ChartCard, 'series' | 'categories' | 'totals'>,
  timeframe: Timeframe | undefined
): number | undefined => {
  if (timeframe !== 'Annual') return undefined
  const value = findSeries(referenceSeries(card.series), OUTLOOK_SERIES)?.data[
    latestDataIndex(card)
  ]
  return typeof value === 'number' ? value : undefined
}

const signedDiff = (gap: number, unit: string | null | undefined) =>
  unit === 'EUR' ? `${gap > 0 ? '+' : ''}${formatAbbrev(gap, unit, 1)}` : null

/**
 * The expanded card's status sections:
 * the latest reported value against each benchmark of the same period, and
 * (in the YTD view) the year-end outlook against the revised budgets. The
 * card is expected to have its budgets masked by period (see `maskBudgets`). A row
 * with no figure to compare, and a section left without rows or a total, are
 * omitted.
 */
export const statusSections = (
  card: Pick<ChartCard, 'series' | 'totals' | 'categories' | 'unit'>,
  timeframe: Timeframe = 'Monthly',
  /** The bar the panel speaks of; the latest reported one when unset. */
  selected?: number
): StatusSection[] => {
  const viewIdx = selected ?? latestDataIndex(card)
  if (viewIdx < 0) return []
  const metrics = metricSeries(card.series)
  const references = referenceSeries(card.series)
  const current =
    metrics.length > 0
      ? metrics.reduce((sum, entry) => sum + (entry.data[viewIdx] ?? 0), 0)
      : (card.totals[viewIdx] ?? 0)
  const outlook = findSeries(references, OUTLOOK_SERIES)

  // The Annual view, for a year with an outlook, is measured by the outlook.
  const annualOutlook = annualOutlookOf(card, timeframe)
  const deltaBase = annualOutlook ?? current

  const compare = (names: string[], base: number, at: number): StatusRow[] =>
    names.flatMap(name => {
      const target = findSeries(references, name)?.data[at]
      if (typeof target !== 'number' || target === 0) return []
      const gap = base - target
      return [
        {
          label: seriesLabel(name),
          pct: (gap / Math.abs(target)) * 100,
          diff: signedDiff(gap, card.unit),
        },
      ]
    })

  // The Annual view is measured by the forecasts, not the original budgets.
  const deltaRows = compare(
    timeframe === 'Annual'
      ? PANEL_DELTA_SERIES_NAMES.filter(
          name => !name.startsWith('metric_budget_')
        )
      : PANEL_DELTA_SERIES_NAMES,
    deltaBase,
    viewIdx
  )

  const sections: StatusSection[] = []
  switch (timeframe) {
    case 'YTD': {
      const outlookIdx = outlook ? lastNumberIndex(outlook.data) : -1
      const outlookValue = outlook?.data[outlookIdx]
      sections.push(
        {
          title: 'Monthly YTD Status ▲',
          rows: deltaRows,
        },
        {
          title: 'End of Year Outlook and ▲',
          ...(typeof outlookValue === 'number' && {
            total: formatAbbrev(outlookValue, card.unit, 1),
            totalLabel: seriesLabel(OUTLOOK_SERIES),
          }),
          rows:
            typeof outlookValue === 'number'
              ? compare(
                  OUTLOOK_COMPARISON_SERIES_NAMES,
                  outlookValue,
                  outlookIdx
                )
              : [],
        }
      )
      break
    }
    case 'Quarterly':
      sections.push({ title: 'Quarterly Status ▲', rows: deltaRows })
      break
    case 'Annual':
      // The year is named after the calendar, not the viewed category.
      sections.push(
        ...(annualOutlook === undefined
          ? [
              {
                title: `${new Date().getFullYear()} YTD Status`,
                rows: deltaRows,
              },
            ]
          : [
              { title: 'End of Year Status ▲', rows: deltaRows },
              {
                title: 'Monthly YTD Actuals',
                total: formatAbbrev(current, card.unit, 1),
                totalLabel: 'Actuals',
                rows: [],
              },
            ])
      )
      break
    default:
      sections.push({ title: 'Monthly Status ▲', rows: deltaRows })
  }
  return sections.filter(section => section.rows.length > 0 || section.total)
}
